import { useTranslation } from 'react-i18next'
import { Head } from 'vite-react-ssg'
import { Layout } from '@/components/layout/Layout'
import { Hero } from '@/components/sections/Hero'
import { About } from '@/components/sections/About'
import { Services } from '@/components/sections/Services'
import { Process } from '@/components/sections/Process'
import { Projects } from '@/components/sections/Projects'
import { CTABanner } from '@/components/sections/CTABanner'
import { Contact } from '@/components/sections/Contact'

function App() {
  const { i18n } = useTranslation()
  const isGerman = i18n.language === 'de'

  const siteTitle = isGerman
    ? 'Webapp Entwicklung für den Mittelstand | Jonas Fackelmeyer'
    : 'Webapp Development for SMEs | Jonas Fackelmeyer'

  const siteDescription = isGerman
    ? 'Fullstack Entwickler für den Mittelstand. Ich digitalisiere Ihre Geschäftsprozesse mit individuellen Webanwendungen. Kostenlose Erstberatung – Antwort in 24h.'
    : 'Fullstack developer for SMEs. I digitize your business processes with custom web applications. Free consultation – response within 24h.'

  const siteUrl = 'https://fackelmeyer.com'

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Jonas Fackelmeyer - Fullstack Developer Portfolio',
    alternateName: ['Jonas Fackelmeyer', 'Fackelmeyer', 'JF Portfolio'],
    url: siteUrl,
    description: isGerman
      ? 'Freelance Fullstack-Entwickler für individuelle Webapps mit Spring Boot & React'
      : 'Freelance fullstack developer for custom webapps with Spring Boot & React',
    inLanguage: ['de-DE', 'en-US'],
    publisher: {
      '@type': 'Person',
      name: 'Jonas Fackelmeyer',
      url: siteUrl,
    },
  }

  const personJsonLd = {
    '@context': 'https://schema.org',
    '@type': ['Person', 'ProfessionalService'],
    name: 'Jonas Fackelmeyer',
    url: siteUrl,
    description: isGerman
      ? 'Fullstack Developer für den Mittelstand – Digitalisierung von Geschäftsprozessen'
      : 'Fullstack Developer for SMEs – Digitizing business processes',
    jobTitle: 'Fullstack Developer',
    areaServed: {
      '@type': 'Country',
      name: 'Germany',
    },
    priceRange: '$$',
    sameAs: [
      'https://github.com/alugor',
      'https://linkedin.com/in/jonas-fackelmeyer',
    ],
    knowsAbout: [
      'Java',
      'Spring Boot',
      'React',
      'TypeScript',
      'PostgreSQL',
      'Docker',
      'REST APIs',
      'Web Application Development',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: isGerman ? 'Entwicklungsleistungen' : 'Development Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: isGerman ? 'Webapp-Entwicklung' : 'Webapp Development',
            description: isGerman
              ? 'Webanwendungen die Ihre Prozesse automatisieren und Fehler eliminieren'
              : 'Web applications that automate your processes and eliminate errors',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: isGerman ? 'System-Integration' : 'System Integration',
            description: isGerman
              ? 'Verbindung bestehender Software für durchgängigen Datenfluss'
              : 'Connecting existing software for seamless data flow',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: isGerman ? 'Beratung & Optimierung' : 'Consulting & Optimization',
            description: isGerman
              ? 'Analyse und Verbesserung bestehender Systeme'
              : 'Analysis and improvement of existing systems',
          },
        },
      ],
    },
  }

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: isGerman ? 'Was kostet ein Projekt?' : 'What does a project cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: isGerman
            ? 'Die Kosten hängen vom Umfang ab. Ein einfaches Projekt startet bei ca. 2.500€, komplexere Webanwendungen liegen typischerweise zwischen 5.000€ und 15.000€.'
            : 'Costs depend on scope. A simple project starts at around €2,500, while more complex web applications typically range from €5,000 to €15,000.',
        },
      },
      {
        '@type': 'Question',
        name: isGerman ? 'Wie lange dauert die Entwicklung?' : 'How long does development take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: isGerman
            ? 'Ein typisches MVP ist in 4-8 Wochen realisierbar. Komplexere Projekte mit mehreren Features benötigen 3-6 Monate.'
            : 'A typical MVP can be delivered in 4-8 weeks. More complex projects with multiple features require 3-6 months.',
        },
      },
      {
        '@type': 'Question',
        name: isGerman ? 'Wie läuft die Zusammenarbeit ab?' : 'How does the collaboration work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: isGerman
            ? 'Nach dem Erstgespräch erstelle ich ein Konzept und Angebot. Nach Ihrer Freigabe starte ich mit der Entwicklung in agilen Sprints mit regelmäßigen Updates.'
            : 'After the initial call, I create a concept and quote. Once approved, I start development in agile sprints with regular updates.',
        },
      },
    ],
  }

  return (
    <>
      <Head>
        <html lang={i18n.language} />
        <title>{siteTitle}</title>
        <meta name="title" content={siteTitle} />
        <meta name="description" content={siteDescription} />
        <meta name="author" content="Jonas Fackelmeyer" />
        <meta
          name="keywords"
          content={
            isGerman
              ? 'Jonas Fackelmeyer, Freelance Entwickler, Fullstack Developer, Webapp Entwicklung Mittelstand, Digitalisierung Geschäftsprozesse, Individuelle Software, System Integration, Web Anwendung'
              : 'Jonas Fackelmeyer, Freelance Developer, Fullstack Developer, Webapp Development SME, Business Process Digitalization, Custom Software, System Integration, Web Application'
          }
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={siteUrl} />

        {/* hreflang alternate links */}
        <link rel="alternate" hrefLang="de" href={`${siteUrl}/`} />
        <link rel="alternate" hrefLang="en" href={`${siteUrl}/?lang=en`} />
        <link rel="alternate" hrefLang="x-default" href={`${siteUrl}/`} />

        {/* Open Graph / Facebook */}
        <meta property="fb:app_id" content="1067046442239826" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:image" content={`${siteUrl}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={isGerman ? 'de_DE' : 'en_US'} />
        <meta property="og:site_name" content="Jonas Fackelmeyer" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={siteUrl} />
        <meta property="twitter:title" content={siteTitle} />
        <meta property="twitter:description" content={siteDescription} />
        <meta property="twitter:image" content={`${siteUrl}/og-image.png`} />

        {/* Structured Data (JSON-LD) */}
        <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(personJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Head>

      <Layout>
        <Hero />
        <About />
        <Services />
        <Projects />
        <Process />
        <CTABanner />
        <Contact />
      </Layout>
    </>
  )
}

export default App
